import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import moment from '@app/utils/moment'
import { catchFetchDataError } from '@app/utils/performFetchData'
import { DEFAULT_CURRENCY } from '@app/utils/printCurrency'

import type { ApiCurrency } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { currencyCodeResultSelector } from './currency_code'
import { routingDateSelector } from './routing'

export const currenciesSlice = createReduxSlice<{ loaded_at: string; list: ApiCurrency[] }>('currencies')

export const currenciesResultSelector = createSelector([currenciesSlice.selector, routingDateSelector], (slice, date) =>
  createLoadable(c => {
    if (!slice || moment(slice.loaded_at).add(30, 'minutes').isBefore(date)) {
      const timeString = date.format()
      return c.load(
        createPromise('Currencies must be fetched', timeString, async dispatch => {
          const { getCurrencies } = await import('@app/packages/api/v2/actions/currencies')
          const resp = await dispatch(callV2Api(getCurrencies())).catch(catchFetchDataError(dispatch, 'Currencies fetch failed'))
          dispatch(currenciesSlice.set({ loaded_at: timeString, list: resp.data }))
        })
      )
    }
    return slice.list
  })
)

export const currentCurrencyResultSelector = createSelector([currencyCodeResultSelector, currenciesResultSelector], (codeResult, listResult) =>
  createLoadable(c => {
    const code = c.unwrap(codeResult)
    const list = c.unwrap(listResult)
    return (
      list.find(c => c.attributes.iso_code === code) ??
      list.find(c => c.attributes.iso_code === DEFAULT_CURRENCY) ??
      invariant(new Error("Can't find currency"))
    )
  })
)
